import { PlugZap } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaChoiceCardField } from "@/shared/components/forms/ColocaChoiceCardField";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import useTranslations from "@/shared/lang/useTranslations";

import { providersForCorridor, type CorridorIdentity } from "../../constants/paymentRoutes.constants";
import { providerCoverageChips } from "../../utils/routeFormat";

export interface ProviderChoiceFieldProps {
  /** Field name holding the adapter code. Registered in the parent `useForm`. */
  name: string;
  /** Corridor being routed. `null` until one is picked, which is when there is nothing to offer yet. */
  corridor: CorridorIdentity | null;
  /** Active route count per provider code — the neutral "routes N corridors" line. */
  corridorCountByProvider: Record<string, number>;
  /** Provider to leave out — the one already on the corridor, when switching. */
  excludeCode?: string;
  /** Shown instead of the generic empty state when the exclusion is what emptied the list. */
  emptyExcludedText?: string;
}

/**
 * Picks which provider executes a corridor, offering **only the providers whose adapter declares it**.
 *
 * That restriction is the point: `POST /routes` accepts any `provider_code`, but a route whose provider does
 * not declare the corridor passes `ProviderSelectorService` and then fails inside `PayoutService.dispatch` —
 * after the funds are reserved — leaving the payout `CREATED` in the rescue queue. Offering an incapable
 * provider would be offering that outcome.
 *
 * No option ever renders the `provider_code` (rule 12): a provider is identified by the corridors it covers
 * and by how many it already routes. When the corridor leaves exactly one candidate — the normal case today —
 * it is auto-selected, so the operator confirms rather than chooses.
 */
export function ProviderChoiceField({
  name,
  corridor,
  corridorCountByProvider,
  excludeCode,
  emptyExcludedText,
}: ProviderChoiceFieldProps) {
  const { t } = useTranslations("paymentRoutes");
  const { setValue, watch } = useFormContext();
  const selected = watch(name) as string;

  const candidates = useMemo(() => {
    if (!corridor) return [];
    return providersForCorridor(corridor).filter((provider) => provider.code !== excludeCode);
  }, [corridor, excludeCode]);

  // Keep the field coherent with the corridor: auto-select the only candidate, and clear a selection the new
  // corridor no longer allows so a stale code can never be submitted.
  useEffect(() => {
    if (candidates.length === 1 && selected !== candidates[0].code) {
      setValue(name, candidates[0].code, { shouldValidate: false });
      return;
    }
    if (selected && !candidates.some((provider) => provider.code === selected)) {
      setValue(name, "", { shouldValidate: false });
    }
  }, [candidates, selected, setValue, name]);

  const items = useMemo(
    () =>
      candidates.map((provider) => {
        const count = corridorCountByProvider[provider.code] ?? 0;
        return {
          value: provider.code,
          label: t("provider_option_label"),
          description: (
            <span className="flex flex-col gap-0.5">
              <span>
                {t("provider_option_coverage", {
                  corridors: providerCoverageChips(provider.code).join(" · "),
                })}
              </span>
              <span className="text-xs text-muted-foreground">
                {count > 0 ? t("provider_option_meta", { count }) : t("provider_option_meta_none")}
              </span>
            </span>
          ),
        };
      }),
    [candidates, corridorCountByProvider, t],
  );

  // Before a corridor is picked there is nothing to offer, and an empty radio group under a label reads as a
  // broken control rather than as a prerequisite.
  if (!corridor) {
    return (
      <div className="space-y-1.5">
        <ColocaLabel>{t("create_provider_label")}</ColocaLabel>
        <p className="text-sm text-muted-foreground">{t("provider_pick_corridor_first")}</p>
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <ColocaAlert
        variant="warning"
        icon={PlugZap}
        heading={emptyExcludedText ? undefined : t("create_provider_none_title")}
        text={emptyExcludedText ?? t("create_provider_none_description")}
      />
    );
  }

  return (
    <ColocaChoiceCardField
      name={name}
      columns={1}
      items={items}
      label={t("create_provider_label")}
      description={t("create_provider_helper")}
    />
  );
}
